<template>
  <div id="app">
    <header v-if="auth.isAuthenticated" class="topnav">
      <div class="topnav__inner">
        <div class="nav-identity">
          <router-link class="nav-wordmark" :to="logoTarget">TRANSIGO</router-link>
          <template v-if="navCtx.visible && gameName">
            <span class="nav-context__sep">/</span>
            <span class="nav-context__name" :title="gameName">
              <span v-if="dotToken" class="nav-context__dot" :style="{ background: dotToken }" aria-hidden="true"></span>
              <span v-if="dotLabel" class="nav-sr-only">{{ dotLabel }}.</span>
              <router-link v-if="navCtx.sectionLabel" :to="`/scenarios/${route.params.id}`">{{ gameName }}</router-link>
              <template v-else>{{ gameName }}</template>
            </span>
            <template v-if="navCtx.sectionLabel">
              <span class="nav-context__sep">/</span>
              <span class="nav-context__section">{{ navCtx.sectionLabel }}</span>
            </template>
          </template>
        </div>

        <nav class="topnav__links" :class="hasContext ? 'topnav__links--divided' : 'topnav__links--plain'">
          <router-link to="/scenarios">Games</router-link>
          <router-link to="/profile">Profile</router-link>
          <router-link to="/admin">Admin</router-link>
        </nav>

        <div class="topnav__actions" :class="hasContext ? 'topnav__actions--divided' : 'topnav__actions--plain'">
          <NotificationCenter v-if="auth.isAuthenticated" />
          <span v-if="auth.isAuthenticated && player.profile"
                class="token-chip"
                :class="{ 'token-chip--empty': player.profile.tokenBalance <= 0 }"
                title="Token balance">
            {{ player.profile.tokenBalance }} T
          </span>
          <button class="btn btn-ghost btn-sm icon-btn" @click="theme.toggle"
                  :title="theme.theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'">
            <!-- Sun icon (shown in dark mode) -->
            <svg v-if="theme.theme === 'dark'" xmlns="http://www.w3.org/2000/svg"
                 width="16" height="16" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="4"/>
              <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/>
            </svg>
            <!-- Moon icon (shown in light mode) -->
            <svg v-else xmlns="http://www.w3.org/2000/svg"
                 width="16" height="16" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9z"/>
            </svg>
          </button>
          <button class="btn btn-ghost btn-sm" @click="auth.logout">Sign out</button>
        </div>
      </div>
    </header>

    <router-view />
    <TokenGateModal v-if="auth.isAuthenticated" />
  </div>
</template>

<script setup>
import { computed, watch, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useThemeStore } from '@/stores/theme'
import { usePlayerStore } from '@/stores/player'
import { useSessionStore } from '@/stores/session'
import { useScenarioStore } from '@/stores/scenario'
import { resolveHomeTarget, navContext, statusDotToken, statusLabel } from '@/utils/sessionScenario'
import NotificationCenter from '@/components/NotificationCenter.vue'
import TokenGateModal from '@/components/TokenGateModal.vue'
import { Client } from '@stomp/stompjs'
import SockJS from 'sockjs-client'

const route = useRoute()
const auth = useAuthStore()
const theme = useThemeStore()
const player = usePlayerStore()
const session = useSessionStore()
const scenario = useScenarioStore()

const logoTarget = computed(() => resolveHomeTarget(session.selectedId))

// Nav breadcrumb context (issue 412): which game is open, and its status dot.
const navCtx = computed(() => navContext(route))

// scenarioStore.current lingers from whichever game was last fetched — guard
// on id match so navigating game A -> Games index -> game B doesn't flash A's
// name/status on B's routes while the new fetchScenario() is in flight.
const currentMatchesRoute = computed(() => scenario.current?.id === route.params.id)

const gameName = computed(() =>
  currentMatchesRoute.value ? scenario.current.name : session.selectedName
)
const gameStatus = computed(() => (currentMatchesRoute.value ? scenario.current.status : null))
const dotToken = computed(() => statusDotToken(gameStatus.value))
const dotLabel = computed(() => statusLabel(gameStatus.value))
const hasContext = computed(() => navCtx.value.visible && !!gameName.value)

// ---------------------------------------------------------------
// Persistent player-topic WebSocket subscription.
// Ensures token balance updates are received regardless of which
// page the user is on (not just inside a scenario session).
// ---------------------------------------------------------------
let playerWsClient = null

function connectPlayerWs(playerId) {
  disconnectPlayerWs()
  playerWsClient = new Client({
    webSocketFactory: () => new SockJS(import.meta.env.VITE_WS_URL),
    reconnectDelay: 5000,
    onConnect: () => {
      playerWsClient.subscribe(`/topic/player/${playerId}`, msg => {
        try {
          const event = JSON.parse(msg.body)
          if (event.event === 'TOKEN_BALANCE_CHANGED') {
            player.fetchProfile()
          }
        } catch {
          // ignore malformed messages
        }
      })
    },
    // Errors are silently swallowed — the scenario-store WS
    // connection already handles StompError reporting.
    onStompError: () => {},
    onDisconnect: () => {},
  })
  playerWsClient.activate()
}

function disconnectPlayerWs() {
  playerWsClient?.deactivate()
  playerWsClient = null
}

watch(() => auth.isAuthenticated, (isAuth) => {
  if (isAuth) {
    player.fetchProfile()
    if (auth.user?.profile?.sub) {
      connectPlayerWs(auth.user.profile.sub)
    }
  } else {
    disconnectPlayerWs()
  }
}, { immediate: true })

onUnmounted(() => {
  disconnectPlayerWs()
})
</script>

<style scoped>
.topnav {
  position: sticky;
  top: 0;
  z-index: 100;
  height: 64px;
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-border);
}

.topnav__inner {
  display: flex;
  align-items: center;
  max-width: 1240px;
  height: 100%;
  margin: 0 auto;
  padding: 0 var(--space-6);
}

/* Identity row: wordmark + "/ Game / Section" breadcrumb context (issue 412).
   align-items: baseline, NOT center — three type sizes (wordmark, the serif
   separator, the game name) sitting on one baseline is the whole point of
   the change; box-centering them reintroduces the misalignment it fixes. */
.nav-identity {
  display: flex;
  align-items: baseline;
  min-width: 0;
  flex-shrink: 0;
}

.nav-wordmark {
  font-family: var(--font-ui);
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.22em;
  color: var(--color-text);
  text-decoration: none;
}

.nav-context__sep {
  font-family: var(--font-display);
  font-size: 22px;
  line-height: 1;
  color: var(--color-border-strong);
  margin: 0 14px;
  transform: translateY(0.5px); /* optical: the serif slash reads high */
}

.nav-context__name {
  font-family: var(--font-display); /* never nav type — this is the whole idea */
  font-size: 18px;
  font-weight: 500;
  color: var(--color-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 349px;
}

.nav-context__name a {
  color: inherit;
  text-decoration: none;
  border-bottom: 1px solid var(--color-border-strong);
}

.nav-context__name a:hover {
  color: inherit;
}

/* Rides the name's own baseline — inline, never a flex sibling, or it
   re-centres against the tallest item in the row. */
.nav-context__dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: var(--radius-pill);
  vertical-align: 0.06em;
  margin-right: 9px;
}

.nav-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.nav-context__section {
  font-family: var(--font-ui);
  font-size: 0.8125rem;
  color: var(--color-text-secondary);
}

.topnav__links {
  display: flex;
  align-items: center;
  gap: var(--space-1);
}

.topnav__links a {
  padding: 6px 12px;
  border-radius: var(--radius-md);
  font-family: var(--font-ui);
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--color-text-secondary);
  text-decoration: none;
  transition: background-color var(--transition-fast), color var(--transition-fast);
}

.topnav__links a:hover {
  background: var(--color-surface-raised);
  color: var(--color-text);
}

.topnav__links a.router-link-active {
  background: var(--color-surface-raised);
  color: var(--color-text);
  font-weight: 600;
}

/* With context: links cluster hugs the right edge, hairline divider before
   utilities. Without: fixed 40px gap after the wordmark, nav "returns to the
   left" (Games index, Profile, Admin). */
.topnav__links--divided {
  margin-left: auto;
  padding-right: 26px;
  border-right: 1px solid var(--color-border);
}

.topnav__links--plain {
  margin-left: 40px;
}

.topnav__actions {
  display: flex;
  align-items: center;
  gap: var(--space-4);
}

.topnav__actions--divided {
  margin-left: 26px;
}

.topnav__actions--plain {
  margin-left: auto;
}

@media (max-width: 1100px) {
  /* Utilities have no separate text labels in this codebase (theme toggle is
     icon-only, Sign out is a single text button) — nothing to collapse there.
     The context slot is what must never wrap, so it's the one thing that
     shrinks under a narrow viewport. */
  .nav-context__name {
    max-width: 220px;
  }
}

.icon-btn {
  padding: 0.4em;
  line-height: 0;
}

.token-chip {
  display: inline-flex;
  align-items: center;
  font-family: var(--font-mono);
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--color-accent);
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-pill);
  padding: 3px 12px;
  transition: color var(--transition-fast), border-color var(--transition-fast);
}

.token-chip--empty {
  color: var(--color-danger);
  border-color: var(--color-danger);
}
</style>
